import { isDueOn, localDay, type Daily, type GameState, type Habit, type Plant, type Task, type Todo } from '@thicket/core';
import { html, nothing } from 'lit-html';
import { art, iconFit } from '../garden/art/index.js';
import { app, update } from './app.js';
import { streakText } from './names.js';

const hashOf = (s: string) => [...s].reduce((h, ch) => Math.imul(h ^ ch.charCodeAt(0), 16777619) >>> 0, 2166136261);
/** The task's own plant, drawn small: a daily's tree, a habit's bed plant, a to-do's bud or bloom. */
function thumb(s: GameState, p: Plant | undefined, task: Task) {
  if (!p) return nothing;
  const biome = p.biome ?? s.profile.starterBiome;
  let src: string;
  if (p.kind === 'tree') src = iconFit(`task-tree:${p.species}:${p.stage}:${p.leaves}:${biome}`, (ctx) => art.tree(ctx, { x: 16, y: 31, species: p.species, stage: p.stage, sparse: p.leaves === 'sparse', biome, seed: 3, tick: 0, reduce: true }), 32, 32);
  else if (p.kind === 'bed') src = iconFit(`task-bed:${p.stage}:${p.pace}`, (ctx) => art.bedPlant(ctx, { x: 8, y: 15, stage: p.stage, seed: hashOf(p.id), pace: p.pace }), 16, 16);
  else {
    const due = task.type === 'todo' ? task.dueDate : null;
    const today = localDay(app.store.now(), s.profile);
    src = p.bloomed
      ? iconFit(`task-bloom:${p.pace}:${biome}`, (ctx) => art.bloom(ctx, { x: 8, y: 15, pace: p.pace, daysLeft: p.bloomDaysLeft ?? 9, biome, tick: 0, reduce: true }), 16, 16)
      : iconFit(`task-bud:${p.pace}:${!!due && due < today}:${biome}`, (ctx) => art.bud(ctx, { x: 8, y: 15, pace: p.pace, drooping: !!due && due < today, biome }), 16, 16);
  }
  return html`<img class="thumb" src=${src} alt="" width="48" height="48">`;
}

const DIFF_LABEL = { trivial: 'Trivial', easy: 'Easy', medium: 'Medium', hard: 'Hard' } as const;

const plantOf = (s: GameState, taskId: string) => s.plants.find((p) => p.taskId === taskId && p.kind !== 'weed' && !p.goneAt);
const open = (taskId: string) => update(() => (app.sheet = { kind: 'task', taskId }));
const score = (id: string, direction: 'up' | 'down') => app.store.act({ type: 'scoreTask', id, direction });

function leavesText(p: Plant | undefined, recovery: number): string {
  if (!p || p.leaves === 'full') return '';
  return p.streak > 0 ? ` · leaves filling in (${p.streak}/${recovery})` : ' · leaves resting';
}

/* Each task is a card: a strip coloured by difficulty holds its checkbox (or + / − for a habit),
   the text sits in the middle, and the task's own plant is drawn on the right. */

function dailyRow(s: GameState, d: Daily) {
  const p = plantOf(s, d.id);
  const streak = p?.streak ?? 0;
  return html`<li class=${`task d-${d.difficulty}${d.completed ? ' done' : ''}`}>
    <button class="strip" aria-pressed=${d.completed} aria-label=${`${d.completed ? 'Undo' : 'Check off'} ${d.text} (${DIFF_LABEL[d.difficulty]})`}
      @click=${() => score(d.id, d.completed ? 'down' : 'up')}><span class="tick" aria-hidden="true">${d.completed ? '✓' : ''}</span></button>
    <button class="body" @click=${() => open(d.id)}>
      <span class="title">${d.text}</span>
      <span class="meta">${DIFF_LABEL[d.difficulty]} · ${streakText(streak)}<span
        class=${p?.leaves === 'sparse' ? 'leaves-sparse' : ''}>${leavesText(p, app.store.rules.leafRecovery)}</span>${p && p.biome === null ? ' · not planted yet' : ''}</span>
    </button>
    ${thumb(s, p, d)}
  </li>`;
}

function habitRow(s: GameState, h: Habit) {
  const weeds = s.plants.filter((p) => p.kind === 'weed' && p.taskId === h.id && !p.goneAt).length;
  const p = plantOf(s, h.id);
  const counts = [h.up && h.counterUp ? `+${h.counterUp}` : '', h.down && h.counterDown ? `−${h.counterDown}` : ''].filter(Boolean).join(' / ');
  return html`<li class=${`task habit d-${h.difficulty}`}>
    <button class="strip pm" ?disabled=${!h.up} aria-label=${`Plus for ${h.text}`} @click=${() => score(h.id, 'up')}><span class="tick" aria-hidden="true">+</span></button>
    <button class="body" @click=${() => open(h.id)}>
      <span class="title">${h.text}</span>
      <span class="meta">${DIFF_LABEL[h.difficulty]} · ${p?.stage === 'seed' || !p ? 'seed' : `${p.stage} plant`}${counts ? ` · ${counts}` : ''}${weeds ? ` · ${weeds} weed${weeds > 1 ? 's' : ''}` : ''}</span>
    </button>
    ${thumb(s, p, h)}
    ${h.down ? html`<button class="strip pm minus" aria-label=${`Minus for ${h.text}`} @click=${() => score(h.id, 'down')}><span class="tick" aria-hidden="true">−</span></button>` : nothing}
  </li>`;
}

function todoRow(s: GameState, today: string, t: Todo) {
  const drooping = t.dueDate && t.dueDate < today;
  return html`<li class=${`task todo d-${t.difficulty}`}>
    <button class="strip" aria-pressed="false" aria-label=${`Check off ${t.text} (${DIFF_LABEL[t.difficulty]})`} @click=${() => score(t.id, 'up')}><span class="tick" aria-hidden="true"></span></button>
    <button class="body" @click=${() => open(t.id)}>
      <span class="title">${t.text}</span>
      <span class="meta">${DIFF_LABEL[t.difficulty]}${t.dueDate ? html` · <span class=${drooping ? 'late' : ''}>${drooping ? `was due ${t.dueDate}` : `due ${t.dueDate}`}</span>` : ''}</span>
    </button>
    ${thumb(s, plantOf(s, t.id), t)}
  </li>`;
}

/** Counts for the tab bar's badges: dailies left today, and open to-dos. */
export function taskCounts(s: GameState) {
  const today = localDay(app.store.now(), s.profile);
  const dailies = s.tasks.filter((t): t is Daily => t.type === 'daily' && !t.archivedAt && !t.paused && isDueOn(t.repeat, today));
  return {
    due: dailies.length,
    done: dailies.filter((d) => d.completed).length,
    todos: s.tasks.filter((t) => t.type === 'todo' && !t.archivedAt && !t.completed).length,
  };
}

/** One of the three task tabs: Habits, Dailies or To-dos. */
export function tasksView(s: GameState, kind: 'habits' | 'dailies' | 'todos') {
  const today = localDay(app.store.now(), s.profile);
  const live = (t: Task) => !t.archivedAt;
  const byPos = (a: Task, b: Task) => a.position - b.position;
  const paused = s.profile.pause;
  const resting = paused ? html`<div class="box banner"><div class="section-head"><h3>Resting</h3>
      <button @click=${() => app.store.act({ type: 'resume' })}>Resume</button></div>
      <p class="small" style="margin:0">Nothing changes while you rest${paused.until ? `, until ${paused.until}` : ''}. Streaks and flowers wait for you.</p></div>` : nothing;

  if (kind === 'dailies') {
    const dailies = s.tasks.filter((t): t is Daily => t.type === 'daily' && live(t)).sort(byPos);
    const due = dailies.filter((d) => !d.paused && isDueOn(d.repeat, today));
    const other = dailies.filter((d) => !due.includes(d));
    return html`${resting}
      <section id="dailies">
        <ul class="list">${due.length ? due.map((d) => dailyRow(s, d)) : html`<li class="empty">Nothing due today. Tap + to add a daily and plant a tree.</li>`}</ul>
        ${other.length ? html`<details class="later"><summary>Not due today · ${other.length}</summary>
          <ul class="list">${other.map((d) => dailyRow(s, d))}</ul></details>` : nothing}
      </section>`;
  }
  if (kind === 'habits') {
    const habits = s.tasks.filter((t): t is Habit => t.type === 'habit' && live(t)).sort(byPos);
    return html`${resting}
      <section id="habits">
        <ul class="list">${habits.length ? habits.map((h) => habitRow(s, h)) : html`<li class="empty">Habits grow vegetables and herbs in your raised bed. Tap + to add one.</li>`}</ul>
      </section>`;
  }
  const todos = s.tasks.filter((t): t is Todo => t.type === 'todo' && live(t) && !t.completed).sort(byPos);
  return html`${resting}
    <section id="todos">
      <ul class="list">${todos.length ? todos.map((t) => todoRow(s, today, t)) : html`<li class="empty">To-dos bloom into wildflowers when you finish them. Tap + to add one.</li>`}</ul>
    </section>`;
}
